import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, API } from "../../App";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Calendar } from "../ui/calendar";
import { ScrollArea } from "../ui/scroll-area";
import { Clock, Trash2, Search, CalendarIcon, Wrench } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

export const LogsManagement = () => {
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const { token } = useAuth();
  
  // Filters state
  const [filterUser, setFilterUser] = useState("all");
  const [filterTool, setFilterTool] = useState("all");
  const [filterDateFrom, setFilterDateFrom] = useState(null);
  const [filterDateTo, setFilterDateTo] = useState(null);
  const [searchText, setSearchText] = useState("");

  const fetchLogs = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/logs?limit=500`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLogs(response.data.logs);
      setTotal(response.data.total);
    } catch (error) {
      toast.error("Erreur lors du chargement des logs");
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      console.error("Error fetching users");
    }
  }, [token]);

  useEffect(() => {
    fetchLogs();
    fetchUsers();
  }, [fetchLogs, fetchUsers]);

  // Apply filters
  useEffect(() => {
    let result = [...logs];
    
    // Filter by user
    if (filterUser !== "all") {
      result = result.filter(log => (log.user_login || log.username) === filterUser);
    }
    
    // Filter by tool
    if (filterTool !== "all") {
      result = result.filter(log => log.tool_name === filterTool);
    }
    
    // Filter by date range
    if (filterDateFrom) {
      const fromDate = new Date(filterDateFrom);
      fromDate.setHours(0, 0, 0, 0);
      result = result.filter(log => new Date(log.created_at) >= fromDate);
    }
    if (filterDateTo) {
      const toDate = new Date(filterDateTo);
      toDate.setHours(23, 59, 59, 999);
      result = result.filter(log => new Date(log.created_at) <= toDate);
    }
    
    // Filter by search text
    if (searchText.trim()) {
      const search = searchText.toLowerCase();
      result = result.filter(log => {
        const input = typeof log.input_data === 'object' ? JSON.stringify(log.input_data) : (log.input_data || '');
        const output = typeof log.output_data === 'object' ? JSON.stringify(log.output_data) : (log.output_data || '');
        const user = log.user_login || log.username || '';
        return input.toLowerCase().includes(search) ||
               output.toLowerCase().includes(search) ||
               user.toLowerCase().includes(search);
      });
    }
    
    setFilteredLogs(result);
  }, [logs, filterUser, filterTool, filterDateFrom, filterDateTo, searchText]);

  const clearLogs = async () => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer tous les logs ?")) return;
    try {
      await axios.delete(`${API}/admin/logs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Logs supprimés");
      fetchLogs();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const clearFilters = () => {
    setFilterUser("all");
    setFilterTool("all");
    setFilterDateFrom(null);
    setFilterDateTo(null);
    setSearchText("");
  };

  const formatDate = (dateStr) => {
    try {
      return format(new Date(dateStr), "dd/MM/yyyy HH:mm:ss", { locale: fr });
    } catch {
      return dateStr;
    }
  };

  // Get unique tools from logs
  const uniqueTools = [...new Set(logs.map(log => log.tool_name))].filter(Boolean);
  // Get unique users from logs
  const uniqueUsers = [...new Set(logs.map(log => log.user_login || log.username).filter(Boolean))];

  const hasActiveFilters = filterUser !== "all" || filterTool !== "all" || filterDateFrom || filterDateTo || searchText.trim();

  return (
    <div className="p-6 animate-fade-in" data-testid="logs-management">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Historique</h1>
          <p className="text-muted-foreground text-sm mt-1">
            {hasActiveFilters ? `${filteredLogs.length} résultat(s) sur ` : ""}{total} action{total > 1 ? 's' : ''} enregistrée{total > 1 ? 's' : ''}
          </p>
        </div>
        {logs.length > 0 && (
          <Button 
            variant="outline" 
            onClick={clearLogs}
            className="text-destructive hover:bg-destructive/10"
            data-testid="clear-logs-button"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Vider l'historique
          </Button>
        )}
      </div>

      {/* Filters Section */}
      <Card className="bg-card border-border mb-6">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-4">
            <Search className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">Filtres</span>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="ml-auto text-xs">
                Réinitialiser
              </Button>
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Search text */}
            <div className="space-y-1">
              <Label className="text-xs">Recherche</Label>
              <Input
                placeholder="Rechercher..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="h-9"
                data-testid="filter-search"
              />
            </div>
            
            {/* User filter */}
            <div className="space-y-1">
              <Label className="text-xs">Utilisateur</Label>
              <Select value={filterUser} onValueChange={setFilterUser}>
                <SelectTrigger className="h-9" data-testid="filter-user">
                  <SelectValue placeholder="Tous" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les utilisateurs</SelectItem>
                  {uniqueUsers.map(user => (
                    <SelectItem key={user} value={user}>{user}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Tool filter */}
            <div className="space-y-1">
              <Label className="text-xs">Outil</Label>
              <Select value={filterTool} onValueChange={setFilterTool}>
                <SelectTrigger className="h-9" data-testid="filter-tool">
                  <SelectValue placeholder="Tous" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les outils</SelectItem>
                  {uniqueTools.map(tool => (
                    <SelectItem key={tool} value={tool}>{tool}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Date from */}
            <div className="space-y-1">
              <Label className="text-xs">Date début</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full h-9 justify-start text-left font-normal" data-testid="filter-date-from">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {filterDateFrom ? format(filterDateFrom, "dd/MM/yyyy") : "Sélectionner"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={filterDateFrom}
                    onSelect={setFilterDateFrom}
                    locale={fr}
                  />
                </PopoverContent>
              </Popover>
            </div>
            
            {/* Date to */}
            <div className="space-y-1">
              <Label className="text-xs">Date fin</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full h-9 justify-start text-left font-normal" data-testid="filter-date-to">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {filterDateTo ? format(filterDateTo, "dd/MM/yyyy") : "Sélectionner"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={filterDateTo}
                    onSelect={setFilterDateTo}
                    locale={fr}
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="bg-card border-border animate-pulse">
              <CardContent className="p-4">
                <div className="h-4 bg-muted rounded w-1/3 mb-2" />
                <div className="h-3 bg-muted rounded w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filteredLogs.length === 0 ? (
        <Card className="bg-card border-border">
          <CardContent className="p-8 text-center">
            <Clock className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">
              {hasActiveFilters ? "Aucun résultat" : "Aucun historique"}
            </h3>
            <p className="text-muted-foreground">
              {hasActiveFilters ? "Essayez de modifier vos filtres." : "Les actions utilisateurs apparaîtront ici."}
            </p>
          </CardContent>
        </Card>
      ) : (
        <ScrollArea className="h-[calc(100vh-380px)]">
          <div className="space-y-3">
            {filteredLogs.map((log, index) => (
              <Card 
                key={log.id || `log-${index}`} 
                className="bg-card border-border hover:border-primary/20 transition-colors"
                data-testid={`log-item-${log.id || index}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-primary/10 rounded-sm">
                        <Wrench className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="font-semibold">{log.tool_name}</p>
                        <p className="text-sm text-muted-foreground">
                          par <span className="text-accent font-mono">{log.user_login || log.username || "Inconnu"}</span>
                        </p>
                      </div>
                    </div>
                    <Badge variant="outline" className="font-mono text-xs">
                      {formatDate(log.created_at)}
                    </Badge>
                  </div>
                  
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <p className="text-xs text-muted-foreground uppercase tracking-wider">Entrée</p>
                      <div className="bg-muted/30 p-2 rounded-sm">
                        <p className="font-mono text-xs break-all text-foreground/80">
                          {typeof log.input_data === 'object' 
                            ? JSON.stringify(log.input_data, null, 2) 
                            : log.input_data}
                        </p>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs text-muted-foreground uppercase tracking-wider">Sortie</p>
                      <div className="bg-muted/30 p-2 rounded-sm">
                        <pre className="font-mono text-xs text-primary whitespace-pre-wrap">
                          {typeof (log.output_data || log.result) === 'object' 
                            ? JSON.stringify(log.output_data || log.result, null, 2) 
                            : (log.output_data || log.result || "—")}
                        </pre>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </ScrollArea>
      )}
    </div>
  );
};

